import { Box, TextField } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import OtpInput from "react-otp-input";

const OTPForm = ({ phoneNumber, email, fullName }) => {
  const navigate = useNavigate();
  const [otp, setOtp] = React.useState("".padStart(6, ""));
  const location = useLocation();
  const [timer, setTimer] = React.useState(120); // Initial timer is 2 minutes
  const [isResendDisabled, setIsResendDisabled] = React.useState(false);
  const [otpError, setOtpError] = useState(false);

  // wrapper ref to find inputs
  const otpWrapperRef = useRef(null);

  // small helper to reliably focus the first OTP input
  const focusFirstInput = () => {
    // delay slightly to ensure inputs are mounted
    setTimeout(() => {
      const first = otpWrapperRef.current?.querySelector(".input_number");
      if (first) {
        first.focus();
        // place cursor at end (if any)
        if (typeof first.setSelectionRange === "function") {
          const len = first.value?.length || 0;
          first.setSelectionRange(len, len);
        }
      }
    }, 100);
  };

  // focus on mount
  useEffect(() => {
    focusFirstInput();
  }, []);

  // timer logic (unchanged)
  useEffect(() => {
    let countdown;
    if (timer > 0) {
      countdown = setTimeout(() => setTimer((prev) => prev - 1), 1000);
      setIsResendDisabled(true);
    } else {
      setIsResendDisabled(false);
    }
    return () => clearTimeout(countdown);
  }, [timer]);

  const handleOtpClick = () => {
    const searchParams = new URLSearchParams(location.search);
    const from = searchParams.get("from");
    if (from === "login") {
      axios
        .post(
          `${process.env.REACT_APP_BE_BASE_URL}user/login?action=otp_verify`,
          {
            phone_number:
              process.env.REACT_APP_COUNTRY_CODE === "+972"
                ? `+972${phoneNumber
                    ?.replace(/^\+972/, "")
                    .replace(/^0/, "")
                    .replaceAll("-", "")
                    .trim()}`
                : `+91${phoneNumber
                    ?.replace(/^\+91/, "")
                    .replace(/^0/, "")
                    .replaceAll("-", "")
                    .trim()}`,
            otp: otp,
          }
        )
        .then((res) => {
          toast.success("OTP verified successfully, redirecting to dashboard!");
          localStorage.setItem("authToken", res.data.accessToken);
          localStorage.setItem("refreshAuthToken", res.data.refreshToken);
          navigate("/");
        })
        .catch(() => {
          setOtp("");
          setOtpError(true);
          focusFirstInput();
          setTimeout(() => {
            setOtpError(false);
          }, 4000);
          toast.error("OTP verification failed, please enter a valid OTP!");
        });
    } else {
      axios
        .post(`${process.env.REACT_APP_BE_BASE_URL}user/register?step=2`, {
          email: email,
          otp: otp,
        })
        .then((response) => {
          toast.success("OTP verified successfully!");
          localStorage.setItem("authToken", response.data.accessToken);
          localStorage.setItem("refreshAuthToken", response.data.refreshToken);
          navigate("/register?step=2", {
            state: { email, phoneNumber, fullName },
          });
        })
        .catch(() => {
          setOtp("");
          setOtpError(true);
          focusFirstInput();
          setTimeout(() => {
            setOtpError(false);
          }, 4000);
          toast.error("OTP verification failed, please enter a valid OTP!");
        });
    }
  };

  const resendOtp = () => {
    axios
      .post(`${process.env.REACT_APP_BE_BASE_URL}user/resend-otp`, {
        phone_number:
          process.env.REACT_APP_COUNTRY_CODE === "+972"
            ? `+972${phoneNumber
                .replace(/^\+972/, "")
                .replace(/^0/, "")
                .replaceAll("-", "")
                .trim()}`
            : `+91${phoneNumber
                .replace(/^\+91/, "")
                .replace(/^0/, "")
                .replaceAll("-", "")
                .trim()}`,
      })
      .then(() => {
        toast.success("OTP resent successfully!");
        setTimer(120);
        setIsResendDisabled(true);
        focusFirstInput();
      })
      .catch(() => {
        toast.error("Failed to resend OTP, please try again!");
      });
  };

  useEffect(() => {
    if (otp && otp.length === 6) {
      handleOtpClick();
    }
  }, [otp]);

  useEffect(() => {
    if (!("OTPCredential" in window)) return;
    const abortController = new AbortController();
    navigator.credentials
      .get({
        otp: { transport: ["sms"] },
        signal: abortController.signal,
      })
      .then((otpCredential) => {
        if (otpCredential && otpCredential.code) {
          setOtp(otpCredential.code);
        }
      })
      .catch((err) => {
        console.log("Web OTP not available or user denied permission:", err);
      });

    return () => abortController.abort();
  }, []);

  return (
    <div className="form_box">
      <h2 className="box_head">
        הגיע הזמן לעשות סדר <br />
        בפנסיה ובביטוחים שלך
      </h2>
      <p className="mob-info-p">
        <span className="mob-info-span">שלחנו לך את קוד האימות למספר</span>{" "}
        {phoneNumber}
      </p>

      <div className="otp_timeDiv">
        <p className="login_label">קוד כניסה</p>
      </div>
      <Box
        ref={otpWrapperRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          justifyContent: "space-between",
          direction: "ltr",
        }}
      >
        <OtpInput
          value={otp}
          onChange={setOtp}
          numInputs={6}
          renderSeparator={null}
          renderInput={(props) => (
            <input
              {...props}
              className="input_number"
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              style={{
                width: "15%",
                height: "60px",
                textAlign: "center",
                fontSize: "18px",
                border: "1px solid #ccc",
                borderRadius: "8px",
              }}
              dir="ltr"
            />
          )}
        />
      </Box>
      {otpError ? (
        <div
          className="error_text"
          style={{ marginTop: "6px", textAlign: "right" }}
        >
          אימות הקוד נכשל, נא להזין קוד תקף!
        </div>
      ) : null}
      <div className="otp_timeDiv justify-content-center">
        <span className="timer">
          {String(Math.floor(timer / 60)).padStart(2, "0")}:
          {String(timer % 60).padStart(2, "0")}
        </span>
      </div>

      <button
        className="login_btn"
        onClick={handleOtpClick}
        disabled={isResendDisabled}
        style={{
          backgroundColor: isResendDisabled ? "#1b7870" : "gray",
          color: "white",
          cursor: isResendDisabled ? "pointer" : "not-allowed",
        }}
      >
        המשך
      </button>
      <div className="re_otp">
        <span className="re_otp_des">לא קיבלת את הקוד? </span>
        <button
          className="re_otp_link"
          onClick={resendOtp}
          disabled={isResendDisabled}
          style={{
            color: isResendDisabled ? "gray" : "black",
            cursor: isResendDisabled ? "not-allowed" : "pointer",
          }}
        >
          שלח שוב
        </button>
      </div>
    </div>
  );
};

export default OTPForm;
